import { Component, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Auth, sendPasswordResetEmail } from '@angular/fire/auth';
import { ActivatedRoute } from '@angular/router';
import { catchError, EMPTY, finalize, from, of, startWith, switchMap, take, tap } from 'rxjs';
import { AppConfig } from '../../../../config/appConfig';
import { AuthService } from '../../../services/auth.service';
import { FirestoreService } from '../../../services/firestore.service';
import { PublicBrandingService } from '../../../services/public-branding.service';

@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrl: './password-reset.component.css'
})
export class PasswordResetComponent {
  title = AppConfig.title;
  resetForm: FormGroup;
  readonly user$ = this.authService.user$;
  readonly brandingProfile = toSignal(this.publicBrandingService.brandingProfile$, { initialValue: null });
  readonly isSubmitting = signal(false);
  readonly resetSuccess = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly emailPreview;
  readonly loginLinkQueryParams: Record<string, string> | null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly auth: Auth,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly firestoreService: FirestoreService,
    private readonly publicBrandingService: PublicBrandingService
  ) {
    const redirectTo = this.route.snapshot.queryParamMap.get('redirectTo');
    const accountAlias = this.route.snapshot.queryParamMap.get('ref');

    this.resetForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.emailPreview = toSignal(
      this.resetForm.get('email')!.valueChanges.pipe(startWith(this.resetForm.get('email')!.value ?? '')),
      { initialValue: '' }
    );

    const params: Record<string, string> = {};
    if (redirectTo) {
      params['redirectTo'] = redirectTo;
    }
    if (accountAlias) {
      params['ref'] = accountAlias;
    }
    this.loginLinkQueryParams = Object.keys(params).length ? params : null;
  }

  onReset(): void {
    if (this.resetForm.invalid || this.isSubmitting()) return;

    const { email } = this.resetForm.value;

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    from(sendPasswordResetEmail(this.auth, email)).pipe(
      switchMap(() =>
        this.authService.user$.pipe(
          take(1),
          switchMap(user =>
            user
              ? this.firestoreService.logUserActivity(user.uid, 'password-reset', { email })
              : of(null)
          )
        )
      ),
      tap(() => this.resetSuccess.set(true)),
      catchError(err => {
        console.error('Password reset error:', err);
        this.errorMessage.set(err?.message ?? 'Failed to send password reset email.');
        return EMPTY;
      }),
      finalize(() => this.isSubmitting.set(false))
    ).subscribe();
  }

  resetFormState(): void {
    this.resetSuccess.set(false);
    this.errorMessage.set(null);
    this.resetForm.reset();
  }

  get logoUrl(): string {
    const logoUrl = this.brandingProfile()?.companyInformation?.logoUrl?.trim() ?? '';
    return logoUrl || 'assets/yachtapp-logo-dark.png';
  }

  get logoAlt(): string {
    const companyName = this.brandingProfile()?.companyInformation?.name?.trim() ?? '';
    return companyName ? `${companyName} Logo` : `${this.title} Logo`;
  }
}
